/* Live per-country free-shipping threshold for server-rendered copy.
 *
 * On the English root the server does not know the visitor's country on a cold
 * load, so every "Free shipping above €X" line is baked for the default market
 * (€50). The visitor's real country arrives client side from
 * assets/syba-geo-country-bridge.js (window.SybaDetectedCountry + the
 * 'syba:geo-country' event) and the per-market table from
 * snippets/nf-market-tiers.liquid (window.SybaGiftConfigTable). This module
 * re-resolves and rewrites every element marked with [data-ship-live] (the
 * announcement bar's shipping line; the creator buy box has its own renderer
 * that reuses the helpers below).
 *
 * Markup contract (emitted by snippets/nf-free-shipping-live-attrs.liquid):
 *   data-ship-live               marks the element
 *   data-tpl="Free shipping above [amount]"   localized template
 *   data-threshold-cents / data-symbol / data-suffix   server-resolved market
 *
 * A table row is applied only on the default root and only when its currency
 * equals the page currency, exactly as the cart gift engine does.
 *
 * INTELLIGEMS (2026-10-23): on top of the native value (table row, or the
 * server-rendered data-threshold-cents when no row applies, e.g. on /nl-nl/)
 * every element shows min(native, visitor's Intelligems free-shipping group)
 * via window.SybaShipThreshold (assets/syba-ship-threshold.js). This file is
 * loaded synchronously in <head> right after that resolver, so the rewrite
 * runs before the first paint: when the resolver settles (elements already
 * parsed) and, while the document is still parsing, on every [data-ship-live]
 * node the parser inserts (MutationObserver callbacks are microtasks, so they
 * run before the next paint). The server value is kept in data-native-cents.
 */
(function () {
  'use strict';

  function formatAmount(cents, symbol, suffix) {
    var n = Math.round(cents / 100);
    return suffix ? n + ' ' + symbol : symbol + n;
  }

  function resolveRow(iso, table, onRoot, pageCurrency) {
    if (!onRoot || !iso) return null;
    var row = table && table[String(iso).toUpperCase()];
    if (!row || typeof row.shipping_threshold_cents !== 'number') return null;
    if (pageCurrency && row.currency !== pageCurrency) return null;
    return row;
  }

  function onRoot() {
    try { return !(window.Shopify && window.Shopify.routes && window.Shopify.routes.root && window.Shopify.routes.root !== '/'); }
    catch (e) { return true; }
  }
  function pageCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  }
  function cachedIso() {
    try { return window.SybaDetectedCountry || localStorage.getItem('syba_geo_iso') || ''; } catch (e) { return ''; }
  }

  // NATIVE market for the detected country, or null when the server value must stand.
  function marketFor(iso) {
    var row = resolveRow(iso, window.SybaGiftConfigTable, onRoot(), pageCurrency());
    return row ? { thresholdCents: row.shipping_threshold_cents, symbol: row.currency_symbol, suffix: !!row.currency_is_suffix, currency: row.currency } : null;
  }

  // min(native, Intelligems group) when the resolver is present; native otherwise.
  function effective(market) {
    var R = window.SybaShipThreshold;
    if (!market || !R || typeof R.effectiveCents !== 'function') return market;
    var c = R.effectiveCents(market.thresholdCents, market.currency || pageCurrency());
    if (c === market.thresholdCents) return market;
    return { thresholdCents: c, symbol: market.symbol, suffix: market.suffix, currency: market.currency };
  }

  function hasIg() {
    var R = window.SybaShipThreshold;
    return !!(R && typeof R.thresholdCents === 'function' && R.thresholdCents() != null);
  }

  function rememberNative(el) {
    if (el.getAttribute('data-native-cents') == null) {
      var n0 = el.getAttribute('data-threshold-cents');
      if (n0 != null) el.setAttribute('data-native-cents', n0);
    }
  }

  // The server-rendered native market of one element.
  function elementMarket(el) {
    rememberNative(el);
    var cents = parseInt(el.getAttribute('data-native-cents'), 10);
    if (!(cents > 0)) return null;
    return { thresholdCents: cents, symbol: el.getAttribute('data-symbol') || '€', suffix: el.getAttribute('data-suffix') === '1', currency: pageCurrency() };
  }

  function applyTo(el, market) {
    var tpl = el.getAttribute('data-tpl');
    if (!tpl || !market) return false;
    rememberNative(el);
    var next = tpl.replace('[amount]', formatAmount(market.thresholdCents, market.symbol, market.suffix));
    if (el.textContent !== next) el.textContent = next;
    el.setAttribute('data-threshold-cents', String(market.thresholdCents));
    el.setAttribute('data-symbol', market.symbol);
    el.setAttribute('data-suffix', market.suffix ? '1' : '0');
    return true;
  }

  // Country last applied, so an Intelligems change re-applies without losing
  // the geo re-resolution.
  var lastIso = '';

  function applyEl(el, rowMarket) {
    var base = rowMarket || elementMarket(el);
    if (!base) return false;
    return applyTo(el, effective(base));
  }

  function applyAll(iso) {
    if (iso) lastIso = iso;
    var market = marketFor(lastIso);
    // No row and no Intelligems value: the server text stands untouched.
    if (!market && !hasIg()) return 0;
    var els = document.querySelectorAll('[data-ship-live]');
    var n = 0;
    for (var i = 0; i < els.length; i++) if (applyEl(els[i], market)) n++;
    return n;
  }

  // Subscribe to country changes; cb(iso) fires for the cached value (if any) and every event.
  function onCountry(cb) {
    var c = cachedIso();
    if (c) cb(c);
    window.addEventListener('syba:geo-country', function (ev) { if (ev && ev.detail) cb(ev.detail); });
  }

  // While the HTML is still streaming, rewrite [data-ship-live] nodes as the
  // parser inserts them, so a late-parsed element never paints the native
  // value first. Disconnected at DOMContentLoaded.
  function watchParsing() {
    try {
      if (document.readyState !== 'loading' || typeof MutationObserver !== 'function') return;
      var mo = new MutationObserver(function (records) {
        var market = marketFor(lastIso);
        if (!market && !hasIg()) return;
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (!node || node.nodeType !== 1) continue;
            if (node.hasAttribute('data-ship-live')) applyEl(node, market);
            var inner = node.querySelectorAll('[data-ship-live]');
            for (var k = 0; k < inner.length; k++) applyEl(inner[k], market);
          }
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
      document.addEventListener('DOMContentLoaded', function () { mo.disconnect(); });
    } catch (e) { /* best effort; the DOMContentLoaded apply still runs */ }
  }

  window.SybaShipLive = { formatAmount: formatAmount, resolveRow: resolveRow, marketFor: marketFor, effective: effective, applyTo: applyTo, applyAll: applyAll, onCountry: onCountry };

  function init() { onCountry(applyAll); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  // Intelligems settle / change: re-apply at once. In <head> this runs inside
  // the resolver's settle(), i.e. before its pending class comes off.
  if (!lastIso) lastIso = cachedIso();
  if (window.SybaShipThreshold && typeof window.SybaShipThreshold.onChange === 'function') {
    window.SybaShipThreshold.onChange(function () { applyAll(); });
  }
  watchParsing();
})();
